import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { RoomGuestListType } from "@lifeat/shared/data-models/Rooms/RoomGuest";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import LinkButton from "@common/LifeAtButton/Variants/LinkButton";
import LifeAtDropdownSelector from "@common/LifeAtDropdownSelector/LifeAtDropdownSelector";
import LifeAtDropdownSelectorElement from "@common/LifeAtDropdownSelector/LifeAtDropdownSelectorElement";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { FontSize } from "@themes/Fonts";
import { MEMBERS_LIST_TO_FETCH_PAGE_SIZE } from "../../RoomConstants";
import { useRoomContext } from "../../RoomContext";
import { MemberListFilters, MemberListFiltersFromDb, ModerationState } from "../../RoomMemberTypes";
import { getUsersPublicInfoArray } from "../../RoomService";
import { RoomParams } from "../../RoomTypes";
import { useMembersModalLists } from "../../hooks/useMembersModalLists";
import { RoomPublicUserInfoList } from "../../room-chat/RoomChatTypes";
import { RoomMember } from "./RoomMember";

const StyledDropdownSelector = styled(LifeAtDropdownSelector)`
    padding: 10px 0;
`;

const DropdownSelectorElement = styled.div`
    padding: 8px 10px;
    width: 100%;
`;

const LoadMoreButtonContainer = styled.div`
    margin-top: 10px;
    width: 100%;
    display: flex;
    justify-content: center;
`;

const LoadMoreButton = styled(LinkButton)`
    color: ${(props) => props.theme.textColorSecondary};
    font-size: ${FontSize.small};
`;

const LoadingIndicatorContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
`;

const LOAD_MORE_BTN_LABEL = "Load more";

const FILTER_DISPLAY_NAME: { [key in MemberListFilters]: string } = {
    online: "Online",
    allMembers: "All Members",
    muted: "Muted",
    banned: "Banned",
};

interface RoomMembersListProps {
    readonly onlineGuests: RoomGuestListType;
    readonly roomOwnerUid: string | undefined;
}
export function RoomMembersList(props: RoomMembersListProps) {
    const { messageAuthors } = useRoomContext();
    const [membersInfo, setMembersInfo] = useState(messageAuthors);
    const { roomId }: RoomParams = useParams();
    const { userData }: UserContextType = useUserContext();
    const [selectedFilter, setSelectedFilter] = useState<MemberListFilters>("online");

    const [isMemberListLoading, setIsMemberListLoading] = useState(false);

    const isHost = isRoomOwnedByUser(roomId, userData);

    const hostFilterOptions: MemberListFilters[] = ["muted", "banned"];
    const filterOptions: MemberListFilters[] = ["online", "allMembers", ...(isHost ? hostFilterOptions : [])];
    const [membersList, memberListPagination] = useMembersModalLists(selectedFilter, roomId, isHost);

    const selectedFilterMembers =
        selectedFilter === "online" ? Object.keys(props.onlineGuests ?? {}) : membersList[selectedFilter]?.list;

    useEffect(() => {
        const uidsToDisplay = selectedFilterMembers;
        const existingMembers = Object.keys(membersInfo);

        const remainingMembers = uidsToDisplay.filter((uid: string) => !existingMembers.includes(uid));

        if (remainingMembers.length > 0) {
            setIsMemberListLoading(true);
            getUsersPublicInfoArray(remainingMembers, props.roomOwnerUid, props.onlineGuests)
                .then((newMembers: RoomPublicUserInfoList) => {
                    setMembersInfo({ ...membersInfo, ...newMembers });
                    setIsMemberListLoading(false);
                })
                .catch(() => {
                    setIsMemberListLoading(false);
                });
        }
    }, [selectedFilter, selectedFilterMembers]);

    const handleLoadMore = () => {
        memberListPagination[selectedFilter as MemberListFiltersFromDb]?.increasePageSize();
    };

    const members: JSX.Element[] = selectedFilterMembers
        .sort()
        .reverse()
        .map((uid: string) => {
            let userState: ModerationState;

            // This checks if the user is either banned or muted
            // Note: The selectedFilter check is to short-circuit the conditional
            // if we already know the user is filtered
            if (selectedFilter === "banned" || membersList["banned"]?.list?.includes(uid)) {
                userState = "banned";
            } else if (selectedFilter === "muted" || membersList["muted"]?.list?.includes(uid)) {
                userState = "muted";
            }

            const showMember = !isMemberListLoading || (isMemberListLoading && !!membersInfo[uid]);
            return <>{showMember && <RoomMember userDisplayInfo={membersInfo[uid]} modState={userState} />}</>;
        });

    const showLoadMoreButton =
        selectedFilter !== "online" &&
        members.length >= memberListPagination[selectedFilter]?.numPages * MEMBERS_LIST_TO_FETCH_PAGE_SIZE;

    return (
        <div>
            <StyledDropdownSelector
                selectedElementId={selectedFilter}
                selectedElementName={FILTER_DISPLAY_NAME[selectedFilter]}
            >
                {filterOptions.map((option: MemberListFilters) => {
                    return (
                        <LifeAtDropdownSelectorElement key={`members-list-filter-option-${option}`}>
                            <DropdownSelectorElement onClick={() => setSelectedFilter(option)}>
                                {FILTER_DISPLAY_NAME[option]}
                            </DropdownSelectorElement>
                        </LifeAtDropdownSelectorElement>
                    );
                })}
            </StyledDropdownSelector>

            {isMemberListLoading && (
                <LoadingIndicatorContainer>
                    <LoadingSpinner size={20} />
                </LoadingIndicatorContainer>
            )}

            {members}

            {showLoadMoreButton && (
                <LoadMoreButtonContainer>
                    <LoadMoreButton label={LOAD_MORE_BTN_LABEL} handleClick={handleLoadMore} />
                </LoadMoreButtonContainer>
            )}
        </div>
    );
}
